/**
 * Back and Forward for a page with unsaved work.
 *
 * Inertia (1.3) swaps the page on the window's popstate: no `inertia:before`
 * is dispatched, and a move within the document fires no `beforeunload`. A
 * page that would lose work registers a guard here while it would, and this
 * gate asks it before Inertia hears of the move.
 *
 * The gate is one popstate listener on the window, installed before Inertia's
 * (app.jsx calls installPopstateGate before createInertiaApp). Engines differ
 * on the order a window runs its own listeners in: the DOM standard runs the
 * capturing ones first (WebKit, jsdom); Chromium runs them in the order they
 * were added. Added first and capturing, the gate runs first in both.
 *
 * A guard is `confirmLeave(event)`: it asks the user and returns true to
 * leave, false to stay. On a stay the gate keeps every later listener from
 * hearing of the move (Inertia's among them) and puts back the history entry
 * the page had when it registered. An entry with no state is a move inside
 * the page (a hash), which Inertia keeps the page for: no guard is asked.
 */
const guards = [];
let installed = false;

/** Asks `confirmLeave` before a Back or Forward leaves; returns the unregister. */
export function registerNavigationGuard(confirmLeave) {
    const guard = { confirmLeave, entry: { state: window.history.state, url: window.location.href } };
    guards.push(guard);
    return () => {
        const index = guards.indexOf(guard);
        if (index !== -1) guards.splice(index, 1);
    };
}

function onPopState(event) {
    if (event.state === null) return;
    // A copy: a guard that answers may unregister itself.
    for (const guard of [...guards]) {
        if (guard.confirmLeave(event)) continue;
        event.stopImmediatePropagation();
        window.history.pushState(guard.entry.state, '', guard.entry.url);
        return;
    }
}

/** Adds the gate's one listener (again only after the returned uninstall). */
export function installPopstateGate() {
    if (installed || typeof window === 'undefined') return () => {};
    installed = true;
    window.addEventListener('popstate', onPopState, true);
    return () => {
        window.removeEventListener('popstate', onPopState, true);
        installed = false;
    };
}
